import { useCallback, useEffect, useState } from "react";
import { api } from "@/api";
import { toast } from "react-toastify";
import { FaAt, FaUserPlus } from "react-icons/fa6";
import { useAppDispatch } from "@/hooks/useAppDispatch.ts";
import { setLocation } from "@/app/store/location/reducer";
import { Renter } from "@/api/renters/renters.ts";
import { Button, useDisclosure } from "@heroui/react";
import { sortAlphabeticallyByKey } from "@/utils";
import { ChooseRenterDataSourceModal } from "@/app/components/modals/renters/renterDataSourceChooseModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useTour } from "@/hooks/useTour";

export function SelectLocation() {
  const dispatch = useAppDispatch();
  const [locations, setLocations] = useState<Renter[] | null>(null);
  const location = useAppSelector((state) => state.location.location);

  const selectSourceDisclosure = useDisclosure();
  const createRenterDisclosure = useDisclosure();

  useTour(0);

  useEffect(() => {
    if (location.id !== 0) {
      dispatch(setLocation({ id: 0, display_name: "Not Selected" }));
      localStorage.removeItem("locationId");
      localStorage.removeItem("locationCachedDisplayName");
      window.location.href = "/dashboard/selectLocation";
    }
  }, []);

  useEffect(() => {
    api.renters
      .list()
      .then((response) => {
        const sortedData = sortAlphabeticallyByKey(
          response.data,
          "display_name",
        );
        setLocations(sortedData);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Произошла ошибка при загрузке арендаторов!");
      });
  }, [setLocations, createRenterDisclosure.isOpen]);

  const selectLocation = useCallback((location: Renter) => {
    localStorage.setItem("locationId", location.id.toString());
    localStorage.setItem("locationCachedDisplayName", location.display_name);
    dispatch(
      setLocation({ id: location.id, display_name: location.display_name }),
    );
    window.location.href = "/dashboard/";
  }, []);

  return (
    <>
      <ChooseRenterDataSourceModal
        disclosure={selectSourceDisclosure}
        nestedDisclosure={createRenterDisclosure}
      />
      <div className="flex flex-col gap-2 p-2 md:p-4">
        <div className="flex flex-row justify-between w-full items-center mb-4">
          <span className="text-2xl font-bold">Выбор компании</span>
          <Button
            id="create-renter-button"
            startContent={<FaUserPlus />}
            color="primary"
            variant="solid"
            className="max-w-fit"
            onClick={() => selectSourceDisclosure.onOpen()}
          >
            Создать
          </Button>
        </div>
        <span className="text-sm max-w-96">
          Вам необходимо выбрать желаемую компанию. Ваш выбор будет сохранён в
          рамках этого браузера и Вы сможете в любой момент выйти из выбраной
          ранее компании и зайти в другую.
        </span>
        <span
          id="companies-selector-title"
          className="text-sm max-w-96 font-medium"
        >
          Ваши компании:
        </span>
        <div
          className="flex flex-row flex-wrap gap-2 max-w-[1000px] mt-2"
          id="companies-selector"
        >
          {locations && locations.length > 0 && (
            <>
              {locations.map((location, index) => (
                <div
                  className="flex flex-col bg-background border-2 border-default-200 rounded-xl cursor-pointer select-none"
                  onClick={() => selectLocation(location)}
                  key={"loc" + index}
                >
                  <div className="flex flex-col gap-3 p-4">
                    <span className="text-medium font-bold">
                      {location.display_name}
                    </span>
                    <div className="flex flex-row gap-2 items-center">
                      <FaAt />
                      <span className="text-sm font-medium">
                        {location.email}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          {!locations ||
            (locations.length == 0 && (
              <span>Похоже, у вас нет доступных арендаторов.</span>
            ))}
        </div>
      </div>
    </>
  );
}
